import { clsx } from 'clsx';
import type { CalendarProps } from 'react-calendar';
import { Calendar } from 'react-calendar';
import type { Dispatch, SetStateAction } from 'react';
import { memo, useContext, useRef, useState } from 'react';
import type { TileArgs, Range, Action } from 'react-calendar/src/shared/types';
import { FormattedMessage, useIntl } from 'react-intl';
import { DateTime } from 'luxon';
import {
    Button,
    ChevronLeftIcon,
    ChevronRightIcon,
    HStack,
    Stack,
    Text,
} from '../../../';
import CheckmarkIcon from '../../../../core/components/icons/CheckmarkIcon';
import LayoutContext from '../../../contexts/LayoutContext';
import type { PredefinedDateRange } from './DateRangeInput';
import styles from './DateInput.module.css';

const DateRangeInputCalendar = ({
    calendarProps,
    localValue,
    onApply,
    onClear,
    predefinedOptions: predefinedOptionsProp,
    tileDisabled,
    value,
    setLocalValue,
}: {
    /**
     * Props that are applied to the calendar component.
     */
    calendarProps?: Pick<
        CalendarProps,
        'minDate' | 'maxDetail' | 'activeStartDate'
    >;
    /**
     * Internal value
     */
    localValue: Range<Date | null> | null;
    onApply(): void;
    onClear(): void;
    /**
     * A list of predefined date range options shown alongside the range picker.
     */
    predefinedOptions?: PredefinedDateRange[];
    /**
     * Compute if tile should be disabled
     */
    tileDisabled?: (props: TileArgs) => boolean;
    /**
     * Current value of the date input.
     */
    value?: Range<Date>;
    /**
     * Set internal value
     */
    setLocalValue: Dispatch<SetStateAction<Range<Date | null> | null>>;
}) => {
    const [isSelecting, setIsSelecting] = useState(false);
    const { isInMobileView } = useContext(LayoutContext);
    const intl = useIntl();

    const dateRangeRef = useRef<{
        setActiveStartDate(nextActiveStartDate: Date, action: Action): void;
    }>();

    const predefinedOptions = predefinedOptionsProp
        ? predefinedOptionsProp.filter(range => {
              return (
                  !calendarProps?.minDate ||
                  range.tentativeStartDate >= calendarProps.minDate
              );
          })
        : [];

    return (
        <div className="dateInput">
            <Stack direction="columnMobileWideRow">
                {predefinedOptions.length > 0 && (
                    <div className={styles.predefinedOptions}>
                        <Stack
                            direction="responsiveRowMobileWideColumn"
                            gap="small"
                        >
                            {predefinedOptions.map((option, idx) => {
                                const isSelected =
                                    !!localValue &&
                                    option.tentativeStartDate ===
                                        localValue[0] &&
                                    option.tentativeEndDate === localValue[1];

                                return (
                                    <Button
                                        key={idx}
                                        outline={!isSelected}
                                        secondary={isSelected}
                                        disabled={isSelecting}
                                        tertiary={!isSelected}
                                        theme={
                                            styles.predefinedOptionButtonTheme
                                        }
                                        onClick={() => {
                                            setLocalValue([
                                                option.tentativeStartDate,
                                                option.tentativeEndDate,
                                            ]);

                                            if (dateRangeRef.current) {
                                                dateRangeRef.current.setActiveStartDate(
                                                    option.tentativeStartDate,
                                                    'onChange'
                                                );
                                            }
                                        }}
                                    >
                                        <div
                                            className={clsx(
                                                styles.predefinedOptionItem,
                                                {
                                                    [styles.hasIcon]:
                                                        isSelected,
                                                }
                                            )}
                                        >
                                            {isSelected && (
                                                <div
                                                    className={
                                                        styles.predefinedOptionItemCheckmark
                                                    }
                                                >
                                                    <CheckmarkIcon />
                                                </div>
                                            )}
                                            <span>{option.label}</span>
                                        </div>
                                    </Button>
                                );
                            })}
                        </Stack>
                    </div>
                )}
                <Calendar
                    {...calendarProps}
                    ref={dateRangeRef}
                    showFixedNumberOfWeeks
                    showNeighboringMonth
                    showDoubleView={!isInMobileView}
                    selectRange
                    className="singleDatePicker"
                    locale={intl.locale}
                    tileDisabled={tileDisabled}
                    onChange={newValue => {
                        setLocalValue(newValue as Range<Date | null> | null);
                    }}
                    onClickDay={() => {
                        setIsSelecting(current => !current);
                    }}
                    value={localValue}
                    minDetail="decade"
                    prevLabel={<ChevronLeftIcon />}
                    nextLabel={<ChevronRightIcon />}
                    prev2Label={null}
                    next2Label={null}
                />
            </Stack>
            {!isInMobileView && (
                <div className={styles.calendarActions}>
                    <HStack align="center" justify="end">
                        <div className={styles.selectedRange}>
                            <Text>
                                {localValue?.[0] && (
                                    <>
                                        {DateTime.fromJSDate(
                                            localValue[0]
                                        ).toFormat('dd.LL.yyyy')}
                                    </>
                                )}
                                {localValue?.[1] && (
                                    <>
                                        {' '}
                                        -{' '}
                                        {DateTime.fromJSDate(
                                            localValue[1]
                                        ).toFormat('dd.LL.yyyy')}
                                    </>
                                )}
                            </Text>
                        </div>
                        <Button text onClick={onApply} disabled={!localValue}>
                            <FormattedMessage id="Library DateInput Apply" />
                        </Button>
                        <Button
                            text
                            secondary
                            disabled={!value}
                            onClick={onClear}
                        >
                            <FormattedMessage id="Library DateInput Clear" />
                        </Button>
                    </HStack>
                </div>
            )}
        </div>
    );
};

export default memo(DateRangeInputCalendar);
